import React, { useState, useEffect } from 'react';
import { useSplash } from '../../../contexts/SplashContext';
import CommonInput from '../../Common/CommonInput';
import CommonDate from '../../Common/CommonDate';
import CommonAsyncSelect from '../../Common/CommonAsyncSelect';
import CommonSelect from '../../Common/CommonSelect';
import { fetchSettings } from '../../../store/modules/stockadjustment/stockadjustmentActions';
import useFormSettings from '../../../hooks/useFormSettings';
import locationService from '../../../services/locationService';
import itemService from '../../../services/itemService';
import { useHasPermission } from '../../../utils/permissionUtils';

const StockAdjustmentForm = ({ formLabel = 'Create Stock Adjustment', initialData, onSave, isLoading, onClose }) => {
    const hasCreatePermission = useHasPermission(['create stock adjustments']);
    const hasUpdatePermission = useHasPermission(['update stock adjustments']);

    const { settings, isLoadingSettings, isSettingsInitialized } = useFormSettings('stockadjustment', fetchSettings);
    // reason_list comes from backend (hardcoded there). TODO: make configurable later
    const { reason_list } = settings || {};

    const [formData, setFormData] = useState({
        id: '',
        adjustment_number: '',
        adjustment_date: new Date().toISOString().split('T')[0],
        location: null,
        item: null,
        quantity: '',
        unit: '',
        unit_cost: '',
        line_amount: '',
        reason: null,
        notes: '',
    });

    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (initialData) {
            setFormData({
                id: initialData.id || '',
                adjustment_number: initialData.adjustment_number || '',
                adjustment_date: initialData.adjustment_date || new Date().toISOString().split('T')[0],
                location: initialData.location || null,
                item: initialData.item || null,
                quantity: initialData.quantity || '',
                unit: initialData.unit || '',
                unit_cost: initialData.unit_cost || '',
                line_amount: initialData.line_amount || '',
                reason: initialData.reason || null,
                notes: initialData.notes || '',
            });
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const updated = { ...prev, [name]: value };
            
            // Recompute line_amount if quantity or unit_cost changes
            if (name === 'quantity' || name === 'unit_cost') {
                const qty = parseFloat(updated.quantity) || 0;
                const cost = parseFloat(updated.unit_cost) || 0;
                updated.line_amount = Math.abs(qty * cost).toFixed(3);
            }
            return updated;
        });

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSelectChange = (name, value) => {
        setFormData(prev => {
            const updated = { ...prev, [name]: value };
            
            if (name === 'item') {
                updated.unit = value ? value.unit : '';
                updated.unit_cost = value ? value.unit_cost : '';
                const qty = parseFloat(updated.quantity) || 0;
                const cost = parseFloat(updated.unit_cost) || 0;
                updated.line_amount = Math.abs(qty * cost).toFixed(3);
            }
            return updated;
        });

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Extra validation
        const newErrors = {};
        if (!formData.location) newErrors.location = 'Location is required';
        if (!formData.item) newErrors.item = 'Item is required';
        if (!formData.quantity || parseFloat(formData.quantity) === 0) newErrors.quantity = 'Quantity must be non-zero';
        if (!formData.unit_cost || parseFloat(formData.unit_cost) < 0) newErrors.unit_cost = 'Unit cost must be positive';
        if (!formData.reason) newErrors.reason = 'Reason is required';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            setIsSaving(true);
            await onSave(formData);
            setErrors({});
            onClose();
        } catch (error) {
            if (error.errors) {
                const apiErrors = {};
                Object.keys(error.errors).forEach(key => {
                    apiErrors[key] = Array.isArray(error.errors[key]) 
                        ? error.errors[key][0] 
                        : error.errors[key];
                });
                setErrors(apiErrors);
            } else {
                setErrors({
                    notes: error.message || 'An error occurred while saving'
                });
            }
        } finally {
            setIsSaving(false);
        }
    };

    const isCreateMode = !formData?.id;
    const hasPermission = isCreateMode ? hasCreatePermission : hasUpdatePermission;
    const isSubmitting = isLoading || isSaving || !hasPermission;

    if (isLoadingSettings || !isSettingsInitialized) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-between mb-3">
                <h5 className="mb-0">{formLabel}</h5>
            </div>
            <div className="flex-grow-1">
                <h6 className="text-primary mb-3">Adjustment Details</h6>
                <div className="row">
                    <div className="col-md-6">
                        <CommonInput
                            name="adjustment_number"
                            label="Adjustment Number"
                            value={formData.adjustment_number || 'Auto-generated'}
                            disabled={true}
                            errors={errors}
                        />
                    </div>
                    <div className="col-md-6">
                        <CommonDate
                            name="adjustment_date"
                            label="Adjustment Date"
                            value={formData.adjustment_date}
                            onChange={handleChange}
                            disabled={true}
                            errors={errors}
                            required={true}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <CommonAsyncSelect
                            name="location"
                            label="Location"
                            value={formData.location}
                            onChange={(value) => handleSelectChange('location', value)}
                            loadOptions={async (inputValue) => {
                                const response = await locationService.getList({ search: inputValue });
                                return response.data.map(loc => ({
                                    id: loc.id,
                                    label: loc.name?.en || loc.name || '',
                                }));
                            }}
                            defaultOptions={true}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                    <div className="col-md-6">
                        <CommonSelect
                            name="reason"
                            label="Reason"
                            value={formData.reason}
                            onChange={(value) => handleSelectChange('reason', value)}
                            options={(reason_list || []).filter(r => r.id !== 'job_order_reversal')}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                            placeholder="Select reason..."
                        />
                    </div>
                </div>

                <h6 className="text-primary mt-4 mb-3">Item & Quantity</h6>
                <div className="row">
                    <div className="col-md-6">
                        <CommonAsyncSelect
                            name="item"
                            label="Item"
                            value={formData.item}
                            onChange={(value) => handleSelectChange('item', value)}
                            loadOptions={async (inputValue) => {
                                const response = await itemService.getList({ search: inputValue });
                                return response.data.map(item => ({
                                    id: item.id,
                                    item_code: item.item_code,
                                    label: (item.name?.en || item.name || '') + ' (' + item.item_code + ')',
                                    unit: item.measurement_unit?.label || '',
                                    unit_cost: item.unit_cost || 0,
                                    available_stock: item.available_stock || 0,
                                }));
                            }}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                    <div className="col-md-6">
                        <CommonInput
                            name="quantity"
                            type="number"
                            step="0.001"
                            label="Quantity (+/-)"
                            value={formData.quantity}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            errors={errors}
                            required={true}
                            placeholder="e.g. 10 or -5"
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <CommonInput
                            name="unit"
                            label="Unit"
                            value={formData.unit}
                            disabled={true}
                            errors={errors}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="unit_cost"
                            type="number"
                            step="0.001"
                            label="Unit Cost"
                            value={formData.unit_cost}
                            onChange={handleChange}
                            disabled={true}
                            errors={errors}
                            required={true}
                            placeholder="0.000"
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="line_amount"
                            label="Line Amount (Computed)"
                            value={formData.line_amount}
                            disabled={true}
                            errors={errors}
                        />
                    </div>
                </div>
                
                <div className="row">
                    <div className="col-md-12">
                        <div className="form-group mb-3">
                            <label className="form-label">Notes</label>
                            <textarea
                                name="notes"
                                className="form-control"
                                rows="3"
                                value={formData.notes}
                                onChange={handleChange}
                                disabled={isSubmitting}
                                placeholder="Enter any additional notes..."
                            />
                            {errors.notes && <div className="text-danger mt-1">{errors.notes}</div>}
                        </div>
                    </div>
                </div>
            </div>
            {hasPermission && (
                <div className="form-footer mt-auto pt-3 border-top d-flex justify-content-end gap-2">
                    <button 
                        type="button" 
                        className="btn btn-secondary" 
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button 
                        type="submit" 
                        className="btn btn-primary" 
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Saving...
                            </>
                        ) : 'Save'}
                    </button>
                </div>
            )}
        </form>
    );
};

export default StockAdjustmentForm;
